import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronRight, Download } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import axiosInstance from '../../services/api/axiosInstance';
import LiquidFillLoader from '../ui/LiquidFillLoader';

const EventDetailsModal = ({ event, teamId = null, onClose }) => {
  const [activeTab, setActiveTab] = useState('timeline');
  const [timeRange, setTimeRange] = useState('24h');
  const [timelineData, setTimelineData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [eventType, setEventType] = useState(null);
  const [exportSuccess, setExportSuccess] = useState(false);

  // Fetch timeline graph data
  useEffect(() => {
    if (activeTab === 'timeline' && event?.eventKey) {
      fetchTimelineData();
    }
  }, [activeTab, timeRange, event]);

  // Fetch raw data
  useEffect(() => {
    if (activeTab === 'rawData' && event?.eventKey) {
      fetchRawData();
    }
  }, [activeTab, event]);

  const fetchTimelineData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        `/event-definitions/timeline/${event.eventKey}?timeRange=${timeRange}`,
        {
          params: teamId ? { teamId } : undefined
        }
      );
      
      if (response.data.success) {
        // Format data for the chart
        const formattedData = response.data.graphData.map(item => ({
          time: formatTimeLabel(item.time, timeRange),
          count: item.count,
          fullTime: item.time
        }));
        setTimelineData(formattedData);
      }
    } catch (err) {
      console.error('Error fetching timeline data:', err);
      setError('Failed to load timeline data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRawData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`/event-definitions/details/${event.eventKey}`, {
        params: teamId ? { teamId } : undefined
      });
      
      if (response.data.success) {
        setRawData(response.data.rawData || []);
        setEventType(response.data.eventType); // Store event type
      }
    } catch (err) {
      console.error('Error fetching raw data:', err);
      setError('Failed to load raw data');
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (index) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const hasParameters = (item) => {
    return eventType === 'form' && item.parameters && Array.isArray(item.parameters) && item.parameters.length > 0;
  };

  const formatParametersForCSV = (item) => {
    if (!hasParameters(item)) {
      return '';
    }
    // Format parameters as "key1: value1, key2: value2"
    return item.parameters.map(p => {
      const value = typeof p.value === 'object' 
        ? JSON.stringify(p.value) 
        : String(p.value);
      return `${p.name}: ${value}`;
    }).join(', ');
  };

  const exportToCSV = () => {
    if (!rawData || rawData.length === 0) {
      return;
    }

    // CSV Headers
    const headers = ['User ID', 'Session ID', 'Geography', 'DateTime', 'Parameters'];
    
    // Convert data to CSV rows
    const csvRows = [
      headers.join(','), // Header row
      ...rawData.map(item => {
        const userId = `"${String(item.userId).replace(/"/g, '""')}"`;
        const sessionId = `"${String(item.sessionId).replace(/"/g, '""')}"`;
        const geography = `"${String(item.geography || '').replace(/"/g, '""')}"`;
        const datetime = `"${new Date(item.datetime).toLocaleString().replace(/"/g, '""')}"`;
        const parameters = `"${formatParametersForCSV(item).replace(/"/g, '""')}"`;
        
        return [userId, sessionId, geography, datetime, parameters].join(',');
      })
    ];

    // Combine rows
    const csvContent = csvRows.join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    // Generate filename with timestamp
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-');
    const timestamp = `${dateStr}_${timeStr}`;
    const safeEventName = event.eventName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const filename = `${safeEventName}_raw_data_${timestamp}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Show success message
    setExportSuccess(true);
    setTimeout(() => setExportSuccess(false), 3000);
  };

  const formatTimeLabel = (isoString, range) => {
    const date = new Date(isoString);
    
    switch (range) {
      case '24h':
      case '1d':
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      case '7d':
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      case '30d':
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      default:
        return date.toLocaleTimeString();
    }
  };

  const getLabelInterval = (dataLength) => {
    if (dataLength <= 7) return 0;
    if (dataLength <= 14) return 1;
    if (dataLength <= 30) return 2;
    return Math.ceil(dataLength / 10);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" style={{ fontFamily: 'Raleway, sans-serif' }}>
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between bg-[#F9FAFB]">
          <div>
            <h2 className="text-xl font-semibold text-[#1d0c46]">
              {event.eventName}
            </h2>
            <p className="text-sm text-[#475569] mt-1">
              Event details and analytics
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[#1d0c46] hover:text-[#361a6f] transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-6 border-b border-[#E5E7EB] px-6">
          <button
            className={`relative pb-3 pt-4 text-sm font-semibold transition-colors ${
              activeTab === 'timeline'
                ? 'text-[#1F2937]'
                : 'text-[#475569] hover:text-[#0f172a]'
            }`}
            onClick={() => setActiveTab('timeline')}
          >
            Timeline Graph
            <span
              className={`absolute left-0 right-0 bottom-0 h-0.5 transition-all ${
                activeTab === 'timeline' ? 'bg-[#1d0c46]' : 'bg-transparent'
              }`}
            />
          </button>
          <button
            className={`relative pb-3 pt-4 text-sm font-semibold transition-colors ${
              activeTab === 'rawData'
                ? 'text-[#1F2937]'
                : 'text-[#475569] hover:text-[#0f172a]'
            }`}
            onClick={() => setActiveTab('rawData')}
          >
            Raw Data
            <span
              className={`absolute left-0 right-0 bottom-0 h-0.5 transition-all ${
                activeTab === 'rawData' ? 'bg-[#1d0c46]' : 'bg-transparent'
              }`}
            />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-6 py-6">
          {activeTab === 'timeline' ? (
            <div>
              {/* Time Range Filter */}
              <div className="flex items-center gap-2 mb-6">
                {[
                  { value: '24h', label: 'Last 24 Hours' },
                  { value: '1d', label: 'Last 1 Day' },
                  { value: '7d', label: 'Last Week' },
                  { value: '30d', label: 'Last Month' }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setTimeRange(option.value)}
                    className={`px-4 py-2 text-sm transition-all duration-200 ${
                      timeRange === option.value
                        ? 'bg-[#1d0c46] text-white'
                        : 'bg-white text-[#1d0c46] border border-[#D1D5DB] hover:bg-[#f0ecff]'
                    }`}
                    style={{ fontFamily: 'Raleway, sans-serif' }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {/* Chart */}
              {loading ? (
                <div className="flex items-center justify-center h-96">
                  <div className="animate-spin h-8 w-8 border-b-2 border-[#1d0c46]"></div>
                  <span className="ml-3 text-sm text-[#475569]">Loading chart...</span>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center h-96">
                  <div className="text-center">
                    <div className="text-[#DC2626] text-sm mb-2">{error}</div>
                    <button
                      onClick={fetchTimelineData}
                      className="text-sm text-[#1d0c46] underline"
                    >
                      Try again
                    </button>
                  </div>
                </div>
              ) : timelineData.length === 0 ? (
                <div className="flex items-center justify-center h-96">
                  <div className="text-center text-sm text-[#475569]">
                    No data available for this time range
                  </div>
                </div>
              ) : (
                <div className="h-96 bg-white border border-[#E5E7EB] p-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={timelineData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                    >
                      <defs>
                        <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1d0c46" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="#1d0c46" stopOpacity={0.1} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#E5E7EB"
                        vertical={true}
                        horizontal={true}
                      />
                      <XAxis
                        dataKey="time"
                        interval={getLabelInterval(timelineData.length)}
                        tick={{
                          fill: '#475569',
                          fontSize: 11,
                          fontFamily: 'Raleway, sans-serif'
                        }}
                        axisLine={{ stroke: '#D1D5DB' }}
                        label={{
                          value: 'Time',
                          position: 'insideBottom',
                          offset: -10,
                          style: {
                            textAnchor: 'middle',
                            fontSize: '12px',
                            fill: '#0f172a',
                            fontWeight: '500'
                          }
                        }}
                      />
                      <YAxis
                        tick={{
                          fill: '#475569',
                          fontSize: 11,
                          fontFamily: 'Raleway, sans-serif'
                        }}
                        axisLine={{ stroke: '#D1D5DB' }}
                        label={{
                          value: 'Event Count',
                          angle: -90,
                          position: 'insideLeft',
                          offset: 0,
                          style: {
                            textAnchor: 'middle',
                            fontSize: '12px',
                            fill: '#0f172a',
                            fontWeight: '500'
                          }
                        }}
                      />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div
                                className="p-3 border border-[#E5E7EB] bg-white shadow-lg"
                                style={{ fontFamily: 'Raleway, sans-serif' }}
                              >
                                <p className="text-sm font-semibold text-[#1F2937] mb-1">
                                  {payload[0].payload.time}
                                </p>
                                <p className="text-sm text-[#475569]">
                                  Events: <span className="font-semibold text-[#1F2937]">{payload[0].value}</span>
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#1d0c46"
                        strokeWidth={2}
                        dot={{ fill: '#1d0c46', strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6 }}
                        isAnimationActive={true}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          ) : (
            <div>
              {/* Raw Data Header with Export Button */}
              {rawData.length > 0 && (
                <div className="flex items-center justify-between mb-4">
                  <div></div>
                  <button
                    onClick={exportToCSV}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#1d0c46] hover:bg-[#361a6f] transition-all duration-200 shadow-sm hover:shadow-md"
                    style={{ fontFamily: 'Raleway, sans-serif' }}
                  >
                    <Download size={16} />
                    Export Data
                  </button>
                </div>
              )}

              {/* Success Toast */}
              {exportSuccess && (
                <div 
                  className="fixed top-4 right-4 bg-green-500 text-white px-4 py-3 rounded-lg shadow-lg z-[9999] flex items-center gap-2 transition-all duration-300 ease-in-out animate-fade-in"
                  style={{ fontFamily: 'Raleway, sans-serif' }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Raw data exported successfully</span>
                </div>
              )}

              {/* Raw Data Table */}
              {loading ? (
                <div className="flex flex-col items-center justify-center h-64">
                  <LiquidFillLoader size={100} />
                  <span className="mt-4 text-sm text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
                    Loading data...
                  </span>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="text-[#DC2626] text-sm mb-2">{error}</div>
                    <button
                      onClick={fetchRawData}
                      className="text-sm text-[#1d0c46] underline"
                    >
                      Try again
                    </button>
                  </div>
                </div>
              ) : rawData.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64">
                  <div className="text-center text-sm text-[#475569] mb-4">
                    No raw data found for this event
                  </div>
                  <button
                    disabled
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#9CA3AF] bg-[#f0ecff] cursor-not-allowed"
                    style={{ fontFamily: 'Raleway, sans-serif' }}
                    title="No data to export"
                  >
                    <Download size={16} />
                    Export Data
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto border border-[#E5E7EB]">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-[#F9FAFB]">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider">
                          User ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider">
                          Session ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider">
                          Geography
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider">
                          DateTime
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {rawData.map((item, index) => {
                        const isExpanded = expandedRows.has(index);
                        const showParameters = hasParameters(item);
                        const rowKey = `${item.sessionId}-${item.datetime}-${index}`;
                        
                        return (
                          <React.Fragment key={rowKey}>
                            <tr 
                              className={`hover:bg-[#F9FAFB] transition-colors ${showParameters ? 'cursor-pointer' : ''}`}
                              onClick={() => showParameters && toggleRow(index)}
                            >
                              <td className="px-4 py-3 text-sm text-[#1F2937] font-mono">
                                <div className="flex items-center gap-2">
                                  {showParameters && (
                                    <span className="text-[#9CA3AF] flex-shrink-0">
                                      {isExpanded ? (
                                        <ChevronDown size={16} />
                                      ) : (
                                        <ChevronRight size={16} />
                                      )}
                                    </span>
                                  )}
                                  {item.userId}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-[#475569] font-mono">
                                {item.sessionId}
                              </td>
                              <td className="px-4 py-3 text-sm text-[#475569]">
                                {item.geography}
                              </td>
                              <td className="px-4 py-3 text-sm text-[#475569]">
                                {new Date(item.datetime).toLocaleString()}
                              </td>
                            </tr>
                            {isExpanded && showParameters && (
                              <tr className="bg-[#F9FAFB]">
                                <td colSpan="4" className="px-4 py-4">
                                  <div className="pl-6 border-l-2 border-[#1d0c46]">
                                    <div className="text-xs font-semibold text-[#475569] uppercase tracking-wider mb-2">
                                      Parameters
                                    </div>
                                    <div className="space-y-2">
                                      {item.parameters.map((param, paramIndex) => (
                                        <div key={paramIndex} className="flex items-start gap-3">
                                          <span className="text-sm font-medium text-[#1F2937] min-w-[120px]">
                                            {param.name}:
                                          </span>
                                          <span className="text-sm text-[#475569] break-words">
                                            {typeof param.value === 'object' 
                                              ? JSON.stringify(param.value, null, 2)
                                              : String(param.value)}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end bg-[#F9FAFB]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-[#1d0c46] hover:bg-[#361a6f] transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventDetailsModal;

